import { InfoCircleOutlined } from '@ant-design/icons';
import { Flex, Tooltip, Typography, theme } from 'antd';
import { twMerge } from 'tailwind-merge';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CardContent, CardShellEcho } from '@/components/cards/common';
import NoDataMessageEcho from '@/components/states/NoDataMessageEcho';
import { useTopPlayersQuery } from '@/services/revenueApi';
import type { PercentageListResponse } from '@/types/api/shows';
import { DevicesPercentageListTooltipContent } from './PercentagesList/DevicesPercentageListTooltipContent';
import { PercentagesListEcho } from './PercentagesList/PercentagesListEcho';
import { TopPlayersPercentageListTooltipContent } from './PercentagesList/TopPlayersPercentageListTooltipContent';
import TopPlayersModalEcho from './TopPlayersModalEcho';
import DataAvailabilityBanner from './DataAvailabilityBanner';
import useAvailabilityQuery from './useAvailabilityQuery';
import {
  isSocialTopPlayerLabel,
  TOP_PLAYER_CATEGORY,
  topPlayersLegend,
} from './topPlayerCategories';

const COLORS = ['#29296B', '#7DA4FF', '#E557BA', '#9CA3AF'];
const PODCAST_PLAYERS_DESCRIPTION = 'By downloads of your podcast on podcast players';
const DOWNLOADS_AND_VIEWS_DESCRIPTION = 'By total downloads or views of all types of content';
const TOP_APPS_ALL_CONTENT_TOOLTIP_TITLE =
  'Includes listens from podcast players and views from YouTube (Shorts & videos), Instagram (Reels, Stories and posts) and TikTok videos';
const legendLabelsWithoutSocials = {
  [TOP_PLAYER_CATEGORY.podcastPlayerApps]: 'Apps',
  [TOP_PLAYER_CATEGORY.webPodcastPlayers]: 'Web',
  [TOP_PLAYER_CATEGORY.other]: 'Other',
};

const PodcastPlayersOnlyTooltipTitle = () => {
  const { token } = theme.useToken();

  return (
    <Flex vertical gap={token.marginXS}>
      <Typography.Text strong style={{ color: token.colorTextLightSolid }}>
        Top apps and websites only available for your audio podcast
      </Typography.Text>
      <Typography.Paragraph style={{ margin: 0, color: token.colorTextLightSolid }}>
        The data presented here only accounts for content consumed on podcast player apps such as
        for instance Spotify, Apple podcast or Castbox.
      </Typography.Paragraph>
      <Typography.Paragraph style={{ margin: 0, color: token.colorTextLightSolid }}>
        It does not account for YouTube, Instagram and TikTok.
      </Typography.Paragraph>
    </Flex>
  );
};

const TopPlayersCardEcho = ({ className }: { className?: string }) => {
  const { token } = theme.useToken();
  const { showAudienceSocialViews } = useFlags<{ showAudienceSocialViews?: boolean }>();
  const showSocialViews = showAudienceSocialViews === true;
  const { showBanner, hideContent, queryResult } = useAvailabilityQuery(
    'players',
    useTopPlayersQuery,
    { excludeSocials: !showSocialViews }
  );

  return (
    <CardShellEcho
      className={twMerge(className, 'flex flex-col gap-1')}
      title="Top apps & websites"
      headerOptions={queryResult.data?.data?.length ? <TopPlayersModalEcho /> : undefined}
    >
      <Typography.Text
        style={{
          color: token.colorTextSecondary,
          marginBottom: token.margin,
        }}
      >
        {showSocialViews ? DOWNLOADS_AND_VIEWS_DESCRIPTION : PODCAST_PLAYERS_DESCRIPTION}{' '}
        <Tooltip
          placement="bottomRight"
          title={
            showSocialViews ? (
              TOP_APPS_ALL_CONTENT_TOOLTIP_TITLE
            ) : (
              <PodcastPlayersOnlyTooltipTitle />
            )
          }
        >
          <InfoCircleOutlined
            style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}
            aria-label="Top apps and websites information"
          />
        </Tooltip>
      </Typography.Text>
      {showBanner && <DataAvailabilityBanner dataType="players" />}
      {!hideContent && (
        <CardContent
          variant="echo"
          {...queryResult}
          renderData={(data) => (
            <Data data={data.data} legend={data.legend} showSocialViews={showSocialViews} />
          )}
        />
      )}
    </CardShellEcho>
  );
};

const Data = ({
  data,
  legend,
  showSocialViews,
}: PercentageListResponse & { showSocialViews: boolean }) => {
  const { token } = theme.useToken();
  const visibleData = showSocialViews
    ? data
    : data
        .filter((item) => !isSocialTopPlayerLabel(item.label))
        .map((item) => ({
          ...item,
          values: item.values.filter(({ label }) => label !== TOP_PLAYER_CATEGORY.socialPlatforms),
        }));
  const visibleLegend = topPlayersLegend(
    showSocialViews
      ? legend
      : legend
          .filter(({ category }) => category !== TOP_PLAYER_CATEGORY.socialPlatforms)
          .map((item) => ({ ...item, description: '' })),
    visibleData
  );

  if (visibleData.length < 1) {
    return <NoDataMessageEcho dataType="data" />;
  }

  const colorMap = Object.fromEntries(
    visibleLegend.map((item, index) => [item.category, COLORS[index % COLORS.length]])
  );

  return (
    <Flex vertical justify="space-between" gap={token.margin} style={{ height: '100%' }}>
      <PercentagesListEcho
        data={visibleData}
        legend={visibleLegend}
        colors={colorMap}
        usePercentage={false}
        legendLabels={showSocialViews ? undefined : legendLabelsWithoutSocials}
        renderTooltipContent={
          showSocialViews
            ? TopPlayersPercentageListTooltipContent
            : DevicesPercentageListTooltipContent
        }
      />
    </Flex>
  );
};

export default TopPlayersCardEcho;
